import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Alert,
  Badge,
  Button,
  Card,
  DataTable,
  Drawer,
  SectionCard,
  Select,
  type ColumnDef,
} from "@/deps/ui-library";
import {
  useFinalizeStatement,
  useReportFrameworks,
  useReportTemplates,
  useStatement,
  useStatementSnapshot,
  useSubscriptionStatus,
} from "@/api/queries";
import type {
  RenderedStatement,
  ReportExport,
  ReportLine,
  StatementOutsider,
  StatementQuery,
} from "@/api/types";
import { useCan } from "@/app/session";
import { SegmentControl } from "@/components/SegmentControl";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";

/** The feature's code in RigoSub's catalogue (ADR-0061). */
const FEATURE = "REPORTING";
const STANDARD = "standard";

const DRAFT_NOTE = "Draft — includes unconfirmed mappings";

/** Which format a statement page shows. Standard needs nothing, so it's always there. */
export function useStatementFormat() {
  const subscription = useSubscriptionStatus();
  const features = subscription.data?.features;
  const entitled = !features || features.includes(FEATURE);
  const { data: frameworks } = useReportFrameworks(entitled);
  const [format, setFormat] = useState(STANDARD);
  return {
    format: entitled ? format : STANDARD,
    setFormat,
    segments: entitled
      ? [
          { value: STANDARD, label: "Standard" },
          ...(frameworks ?? []).map((framework) => ({
            value: framework.code,
            label: framework.name,
          })),
        ]
      : [],
  };
}

export function StatementFormatSwitch({
  format,
  setFormat,
  segments,
}: ReturnType<typeof useStatementFormat>) {
  if (segments.length < 2) return null;
  return <SegmentControl label="Format" segments={segments} value={format} onChange={setFormat} />;
}

/** The statement in a framework's format, for a page to show and to export. */
export function useFrameworkStatement(
  framework: string,
  statement: "pl" | "bs",
  period: Omit<StatementQuery, "templateCode">,
) {
  const on = framework !== STANDARD;
  const { data: templates } = useReportTemplates(on);
  const [chosen, choose] = useState<string | null>(null);
  const available = (templates ?? []).filter(
    (candidate) => candidate.frameworkCode === framework && candidate.statement === statement,
  );
  const template = available.find((candidate) => candidate.code === chosen) ?? available[0];
  const query: StatementQuery = { ...period, templateCode: template?.code ?? "" };
  const rendered = useStatement(query, on && template !== undefined && period.to !== "");
  return { query, rendered, on, available, template, choose };
}

const isDraft = (statement: RenderedStatement) =>
  statement.checks.unconfirmed > 0 || statement.unmapped.length > 0;

/** What Download writes. A draft says so in its title, so it can't be read as final once it's left the screen. */
export function statementExport(statement: RenderedStatement, period: string): ReportExport {
  const compare = statement.compareTo !== null;
  return {
    title: isDraft(statement)
      ? `${statement.templateName} (${DRAFT_NOTE})`
      : statement.templateName,
    period,
    columns: [
      { header: "Line" },
      { header: "Amount", numeric: true },
      ...(compare ? [{ header: "Comparative", numeric: true }] : []),
    ],
    rows: [
      ...statement.lines.map((line) => [
        line.label,
        line.amount,
        ...(compare ? [line.compare] : []),
      ]),
      ...statement.unmapped.map((outsider) => [
        `Unmapped: ${outsider.code} ${outsider.name}`,
        outsider.amount,
        ...(compare ? [null] : []),
      ]),
      ...statement.excluded.map((outsider) => [
        `Excluded: ${outsider.code} ${outsider.name}`,
        outsider.amount,
        ...(compare ? [null] : []),
      ]),
    ],
  };
}

function Banner({ statement }: { statement: RenderedStatement }) {
  const { checks, unmapped } = statement;
  if (checks.difference !== "0" && Number(checks.difference) !== 0) {
    return (
      <Alert
        role="alert"
        config={{ variant: "subtle", colorScheme: "danger" }}
        description={`This differs from the trial balance by ${formatAmount(checks.difference)}, so it can't be finalized.`}
      />
    );
  }

  const excluded =
    Number(checks.excludedTotal) !== 0
      ? ` ${formatAmount(checks.excludedTotal)} sits in excluded accounts, listed below.`
      : "";

  if (!isDraft(statement)) {
    return (
      <Alert
        role="status"
        config={{ variant: "subtle", colorScheme: "success" }}
        description={`Complete. It ties to the trial balance, and every account is confirmed or excluded.${excluded}`}
      />
    );
  }

  const open = checks.unconfirmed - checks.suggested;
  const facts = [
    unmapped.length > 0
      ? `${unmapped.length} ${unmapped.length === 1 ? "account" : "accounts"} with a balance on no line · ${formatAmount(checks.unassignedTotal)} unassigned`
      : null,
    checks.suggested > 0
      ? `${checks.suggested} ${checks.suggested === 1 ? "mapping" : "mappings"} still suggested, marked on the lines they affect`
      : null,
    open > 0 ? `${open} ${open === 1 ? "account" : "accounts"} not mapped yet` : null,
  ].filter(Boolean);

  return (
    <Alert
      role="status"
      config={{ variant: "subtle", colorScheme: "warning" }}
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge colorScheme="warning" size="sm">
            {DRAFT_NOTE}
          </Badge>
          <span>{facts.join(". ")}.</span>
          <span>It ties to the trial balance.{excluded}</span>
          <Link to="/reports/mapping" className="text-primary underline">
            Open account mapping
          </Link>
        </span>
      }
    />
  );
}

function FinalBar({ statement, query }: { statement: RenderedStatement; query: StatementQuery }) {
  const can = useCan();
  const finalize = useFinalizeStatement();
  const [showing, setShowing] = useState(false);
  const final = statement.final;
  const { data: kept } = useStatementSnapshot(final?.id ?? "", showing && final !== null);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        {final ? (
          <span className="text-fg-muted text-xs">
            {`Finalized ${formatDate(final.finalizedAt.slice(0, 10))}`}
            {final.differs
              ? ". The ledger or the mapping has changed since, so it no longer reads like this."
              : ""}
          </span>
        ) : null}
        {final ? (
          <Button variant="secondary" size="sm" onClick={() => setShowing((on) => !on)}>
            {showing ? "Hide the final figures" : "Show the final figures"}
          </Button>
        ) : null}
        {can("reports.layout.edit") && (!final || final.differs) ? (
          <Button
            variant="secondary"
            size="sm"
            leftIcon="Lock"
            disabled={isDraft(statement) || finalize.isPending}
            onClick={() => finalize.mutate(query)}
          >
            {finalize.isPending ? "Finalizing" : "Finalize statement"}
          </Button>
        ) : null}
      </div>
      {finalize.error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(finalize.error as Error).message}
        />
      ) : null}
      {showing && kept ? (
        <SectionCard title={`As finalized · ${kept.templateVersion}`} flush>
          <LinesTable statement={kept} onOpen={() => undefined} />
        </SectionCard>
      ) : null}
    </div>
  );
}

/** A period where there is one, or the one date of a balance sheet. */
const columnHead = (from: string | null, to: string) =>
  from ? `${formatDate(from)} to ${formatDate(to)}` : formatDate(to);

function lineClass(line: ReportLine) {
  return cn(
    line.kind === "grand" && "bg-muted font-semibold",
    (line.kind === "sum" || line.strong) && "font-medium",
    line.kind === "head" && "text-muted-foreground font-medium",
  );
}

function LinesTable({
  statement,
  onOpen,
}: {
  statement: RenderedStatement;
  onOpen: (line: ReportLine) => void;
}) {
  const compare = statement.compareTo !== null;
  const columns = useMemo<Array<ColumnDef<ReportLine>>>(
    () => [
      {
        id: "label",
        header: "Line",
        cell: ({ row: { original: line } }) =>
          line.kind === "tag" ? (
            <span className="flex items-center gap-2 pl-4">
              <Button variant="link" colorScheme="default" size="xs" onClick={() => onOpen(line)}>
                {line.label}
              </Button>
              {line.suggestedAccounts > 0 ? (
                <Badge colorScheme="warning" size="sm">
                  {line.suggestedAccounts} suggested
                </Badge>
              ) : null}
            </span>
          ) : (
            line.label
          ),
      },
      {
        id: "amount",
        header: columnHead(statement.from, statement.to),
        meta: { numeric: true },
        cell: ({ row: { original: line } }) =>
          line.amount === null ? "" : formatAmount(line.amount),
      },
      ...(compare
        ? [
            {
              id: "compare",
              header: statement.compareTo
                ? columnHead(statement.compareFrom, statement.compareTo)
                : "",
              meta: { numeric: true },
              cell: ({ row: { original: line } }: { row: { original: ReportLine } }) =>
                line.compare === null ? "" : formatAmount(line.compare),
            },
          ]
        : []),
    ],
    [compare, onOpen, statement.compareFrom, statement.compareTo, statement.from, statement.to],
  );

  return (
    <DataTable
      aria-label={statement.templateName}
      size="sm"
      enableSorting={false}
      columns={columns}
      data={statement.lines}
      getRowId={(line) => line.code}
      rowClassName={({ original }) => lineClass(original)}
      pagination={{ mode: "manual", show: false }}
      containerClassName="rounded-none border-0"
    />
  );
}

function Outsiders({
  title,
  rows,
  danger,
}: {
  title: string;
  rows: StatementOutsider[];
  danger?: boolean;
}) {
  const columns = useMemo<Array<ColumnDef<StatementOutsider>>>(
    () => [
      { id: "code", header: "Code", cell: ({ row }) => row.original.code },
      { id: "name", header: "Account", cell: ({ row }) => row.original.name },
      { id: "reason", header: "Why", cell: ({ row }) => row.original.reason },
      {
        id: "amount",
        header: "Debit less credit",
        meta: { numeric: true },
        cell: ({ row }) => formatAmount(row.original.amount),
      },
    ],
    [],
  );
  if (rows.length === 0) return null;
  return (
    <SectionCard title={title} note={danger ? "Not included in any line above" : undefined} flush>
      <DataTable
        aria-label={title}
        size="sm"
        enableSorting={false}
        columns={columns}
        data={rows}
        getRowId={(row) => row.accountId}
        pagination={{ mode: "manual", show: false }}
        containerClassName="rounded-none border-0"
      />
    </SectionCard>
  );
}

/** A line, then what adds up to it, then the ledger (design: a side panel, so the statement stays printable). */
function Drill({
  line,
  from,
  to,
  onClose,
}: {
  line: ReportLine;
  /** Null on a balance sheet, whose ledger opens at the year the date falls in. */
  from: string | null;
  to: string;
  onClose: () => void;
}) {
  return (
    <Drawer.Default
      title={line.label}
      rootProps={{
        open: true,
        onOpenChange: (details: { open: boolean }) => !details.open && onClose(),
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="num text-fg text-sm">
          {line.amount === null ? "" : formatAmount(line.amount)}
        </div>
        {line.accounts.length === 0 ? (
          <p className="text-fg-muted text-sm">Nothing moved on this line in the period.</p>
        ) : (
          <ul className="flex flex-col gap-2 text-sm">
            {line.accounts.map((account) => (
              <li key={account.accountId} className="flex items-center justify-between gap-3">
                <span className="flex min-w-0 flex-col">
                  <Link
                    to="/reports/general-ledger"
                    search={{ account: account.code, from: from ?? undefined, to }}
                    className="text-primary underline"
                  >
                    {`${account.code} ${account.name}`}
                  </Link>
                  <span className="text-fg-muted text-xs">{account.tagCode}</span>
                </span>
                <span className="flex items-center gap-2">
                  <Badge
                    colorScheme={account.status === "suggested" ? "warning" : "success"}
                    size="sm"
                  >
                    {account.status === "suggested" ? "Suggested" : "Confirmed"}
                  </Badge>
                  <span className="num text-fg">{formatAmount(account.amount)}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Drawer.Default>
  );
}

/** Which of a framework's formats to read: the supplied one, or a company's own. */
function TemplatePicker({ framework }: { framework: ReturnType<typeof useFrameworkStatement> }) {
  const { available, template, choose } = framework;
  return (
    <div className="flex flex-wrap items-end gap-3">
      {available.length > 1 ? (
        <div className="w-96">
          <Select
            label="Format"
            options={available.map((candidate) => ({
              value: candidate.code,
              label: candidate.name,
            }))}
            value={template?.code ?? ""}
            showClearButton={false}
            onChange={(details) => {
              const value = Array.isArray(details.value) ? details.value[0] : details.value;
              choose(String(value?.value ?? ""));
            }}
          />
        </div>
      ) : null}
      <Link to="/reports/templates" className="text-primary text-xs underline">
        Edit templates
      </Link>
    </div>
  );
}

/** The body of a statement page in a framework's format (ADR-0061). */
export function FrameworkStatementBody({
  framework,
}: {
  framework: ReturnType<typeof useFrameworkStatement>;
}) {
  const { rendered, query } = framework;
  const [open, setOpen] = useState<ReportLine | null>(null);
  const { data, error, isPending } = rendered;

  if (error) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        description={(error as Error).message}
      />
    );
  }
  if (isPending || !data) return <p className="text-fg-muted p-4 text-sm">Loading</p>;

  return (
    <div className="flex flex-col gap-4">
      <TemplatePicker framework={framework} />
      <Banner statement={data} />
      <FinalBar statement={data} query={query} />
      <Card size="none" className="overflow-hidden">
        <LinesTable statement={data} onOpen={setOpen} />
      </Card>
      <Outsiders title="Unmapped" rows={data.unmapped} danger />
      <Outsiders title="Excluded from this framework" rows={data.excluded} />
      <p className="text-fg-muted text-xs">
        {`${data.templateVersion} · supplied format · not a compliance assertion`}
      </p>
      {open ? (
        <Drill line={open} from={data.from} to={data.to} onClose={() => setOpen(null)} />
      ) : null}
    </div>
  );
}
